import { tellerConfigByChain, tellerPoolsByChain } from '@1delta/data-sdk'
import {
  LenderCommitmentGroupAbi,
  TellerMarketRegistryAbi,
  TellerV2Abi,
} from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import type { TellerMarketsRaw, TellerPoolRaw } from './types'

// 7 reads per pool, in this order (keep in sync with the slicer).
const READS_PER_POOL = 7
// 4 reads per unique market (attestation, open, marketplace fee, default window).
const READS_PER_MARKET = 4
// Merged ABI for the market phase — MarketRegistry reads + TellerV2.protocolFee.
const MARKET_PHASE_ABI = [...TellerMarketRegistryAbi, ...TellerV2Abi] as any

/**
 * Fetch all Teller pool data for a chain — FULLY ON-CHAIN (no API/indexer):
 *  1. one retrying multicall reads each curated `LenderCommitmentGroup` pool's
 *     live state (borrowable liquidity, committed principal, min borrow rate,
 *     collateral requirement, max duration, marketId);
 *  2. a second multicall reads each UNIQUE market's `MarketRegistry`
 *     attestation requirement + open flag — so we can surface which pools gate
 *     borrowers (a permissionless integration must know).
 *
 * The pool list comes from lender-metadata (`tellerPools`). Returns an empty
 * batch when the chain has no Teller pools or the read fails (the converter then
 * emits nothing).
 */
export async function fetchTellerMarkets(
  chainId: string,
): Promise<TellerMarketsRaw> {
  const pools = tellerPoolsByChain(chainId)
  if (pools.length === 0) return { chainId, pools: [] }
  const marketRegistry = tellerConfigByChain(chainId)?.marketRegistry

  const calls = pools.flatMap((p) => {
    const onePrincipal = 10n ** BigInt(p.principalDecimals)
    return [
      { address: p.pool, name: 'getPrincipalAmountAvailableToBorrow', params: [] },
      { address: p.pool, name: 'totalPrincipalTokensCommitted', params: [] },
      { address: p.pool, name: 'getMinInterestRate', params: [0n] },
      {
        address: p.pool,
        name: 'calculateCollateralRequiredToBorrowPrincipal',
        params: [onePrincipal],
      },
      { address: p.pool, name: 'getMaxLoanDuration', params: [] },
      { address: p.pool, name: 'getMarketId', params: [] },
      // ERC-4626 CURRENT principal TVL (V2/V3). `totalPrincipalTokensCommitted`
      // is a CUMULATIVE lifetime counter, NOT the current balance — using it
      // overstates deposits by orders of magnitude. `totalAssets` reverts on V1
      // Smart pools (→ null), where we fall back to the committed counter.
      { address: p.pool, name: 'totalAssets', params: [] },
    ]
  })

  let results: any[] = []
  try {
    results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: LenderCommitmentGroupAbi as any,
      allowFailure: true,
    })) as any[]
  } catch {
    return { chainId, pools: [] }
  }

  // Failed (allowFailure) slots arrive as '0x' — mapped to null.
  const big = (i: number): bigint | null => {
    const r = results[i]
    if (typeof r === 'bigint') return r
    if (typeof r === 'number') return BigInt(r)
    if (typeof r === 'string' && r !== '0x') {
      try {
        return BigInt(r)
      } catch {
        return null
      }
    }
    return null
  }
  const num = (i: number): number | null => {
    const b = big(i)
    return b === null ? null : Number(b)
  }

  const pool0: Array<Omit<TellerPoolRaw, 'requiresBorrowerAttestation' | 'marketOpen'> & { marketId: bigint | null }> =
    pools.map((config, i) => {
      const base = i * READS_PER_POOL
      return {
        config,
        available: big(base),
        committed: big(base + 1),
        minRateBps: num(base + 2),
        collateralPerPrincipal: big(base + 3),
        maxLoanDuration: num(base + 4),
        marketId: big(base + 5),
        totalAssets: big(base + 6),
      }
    })

  // Phase 2 — per UNIQUE market (many pools share a market): attestation, open
  // flag, upfront market fee, and the default grace window. Plus the chain-wide
  // TellerV2 protocol fee (one call). Best-effort — flags stay undefined on
  // failure (non-blocking).
  const attByMarket = new Map<
    string,
    {
      requiresBorrowerAttestation: boolean
      marketOpen: boolean
      marketFeeBps?: number
      paymentDefaultDuration?: number
    }
  >()
  let protocolFeeBps: number | undefined
  const proto = tellerConfigByChain(chainId)
  if (marketRegistry) {
    const uniqueMarkets = [
      ...new Set(
        pool0
          .map((p) => (p.marketId != null ? p.marketId.toString() : null))
          .filter((m): m is string => m != null),
      ),
    ]
    if (uniqueMarkets.length > 0) {
      // Prepend the chain-wide protocol fee (on TellerV2), then per-market reads.
      const mCalls = [
        { address: proto?.tellerV2 ?? marketRegistry, name: 'protocolFee', params: [] },
        ...uniqueMarkets.flatMap((m) => [
          {
            address: marketRegistry,
            name: 'getMarketAttestationRequirements',
            params: [BigInt(m)],
          },
          { address: marketRegistry, name: 'isMarketOpen', params: [BigInt(m)] },
          { address: marketRegistry, name: 'getMarketplaceFee', params: [BigInt(m)] },
          {
            address: marketRegistry,
            name: 'getPaymentDefaultDuration',
            params: [BigInt(m)],
          },
        ]),
      ]
      try {
        const mRes = (await multicallRetryUniversal({
          chain: chainId,
          calls: mCalls,
          abi: MARKET_PHASE_ABI,
          allowFailure: true,
        })) as any[]
        const pf = mRes[0]
        protocolFeeBps =
          typeof pf === 'bigint' || typeof pf === 'number' ? Number(pf) : undefined
        uniqueMarkets.forEach((m, i) => {
          const base = 1 + i * READS_PER_MARKET
          const att = mRes[base]
          const open = mRes[base + 1]
          const fee = mRes[base + 2]
          const dur = mRes[base + 3]
          const borrowerRequired =
            (att?.borrowerAttestationRequired ?? att?.[1]) === true
          const asNum = (v: any) =>
            typeof v === 'bigint' || typeof v === 'number' ? Number(v) : undefined
          attByMarket.set(m, {
            requiresBorrowerAttestation: borrowerRequired,
            marketOpen: open === false ? false : true,
            marketFeeBps: asNum(fee),
            paymentDefaultDuration: asNum(dur),
          })
        })
      } catch {
        /* leave flags undefined — non-blocking */
      }
    }
  }

  const out: TellerPoolRaw[] = pool0.map((p) => {
    const att = p.marketId != null ? attByMarket.get(p.marketId.toString()) : undefined
    return {
      ...p,
      requiresBorrowerAttestation: att?.requiresBorrowerAttestation,
      marketOpen: att?.marketOpen,
      marketFeeBps: att?.marketFeeBps,
      protocolFeeBps,
      paymentDefaultDuration: att?.paymentDefaultDuration,
    }
  })

  return { chainId, pools: out }
}
